import { PageHeader, PageShell } from '@/components/PageShell';
import { UserChip } from '@/components/UserChip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLoadMore } from '@rock/db-admin';
import { useDebouncedSearchParam, useSetSearchParam } from '@/lib/searchParams';
import { homeState, homesState } from '@/state/home.state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { type Home, type HomeId, homeLifecycle } from '@rock/types';
import { orderBy } from 'lodash';
import { Star } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const FAVORITES_KEY = 'rock-admin:favorite-homes';

function loadFavorites(): Set<string> {
    try {
        const raw = localStorage.getItem(FAVORITES_KEY);
        return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
        return new Set();
    }
}

function saveFavorites(favorites: Set<string>): void {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify([...favorites]));
}

type HomeCardProps = {
    home: Home;
    isFavorite: boolean;
    onToggleFavorite: (e: React.MouseEvent, homeId: string) => void;
};

const HomeCard: FC<HomeCardProps> = ({ home, isFavorite, onToggleFavorite }) => (
    <Link to={`/directory/homes/${home.id}`} className="block">
        <Card className="h-full transition-shadow hover:shadow-md">
            <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2 text-lg">
                    <span>{home.name}</span>
                    <div className="flex items-center gap-2">
                        {!home.active && (
                            <span className="rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-900">
                                Inactive
                            </span>
                        )}
                        {homeLifecycle(home) !== 'active' && (
                            <span className="rounded bg-sky-100 px-2 py-1 text-xs font-medium text-sky-900">
                                {homeLifecycle(home) === 'setting-up' ? 'Setting up' : 'Reviewing'}
                            </span>
                        )}
                        <button
                            onClick={(e) => onToggleFavorite(e, home.id)}
                            className="cursor-pointer text-muted-foreground hover:text-amber-400 transition-colors"
                            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                        >
                            <Star size={18} className={isFavorite ? 'fill-amber-400 text-amber-400' : ''} />
                        </button>
                    </div>
                </CardTitle>
                <CardDescription className="break-all text-xs">{home.id}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center justify-between">
                    <span className="font-medium text-foreground">ZIP</span>
                    <span>{home.zip}</span>
                </div>
                <div className="flex items-start justify-between gap-2">
                    <span className="font-medium text-foreground">Owner</span>
                    <UserChip userId={home.owner} align="end" showEmail className="max-w-[70%] min-w-0" />
                </div>
            </CardContent>
        </Card>
    </Link>
);

type FavoriteHomeCardProps = {
    homeId: string;
    favorites: Set<string>;
    onToggleFavorite: (e: React.MouseEvent, homeId: string) => void;
};

const FavoriteHomeCard: FC<FavoriteHomeCardProps> = ({ homeId, favorites, onToggleFavorite }) => {
    const home = useAsyncAtomValue(useMemo(() => homeState(homeId as HomeId), [homeId]));
    if (!home) return null;
    return <HomeCard home={home} isFavorite={favorites.has(homeId)} onToggleFavorite={onToggleFavorite} />;
};

const HomeListPage: FC = () => {
    const { data: homes, isLoading, hasError, hasMore, loadMore, filter, setFilter } = useLoadMore(homesState);
    const [favorites, setFavorites] = useState<Set<string>>(loadFavorites);

    // Filters live in the URL so they survive refresh and the row-click → details → back round trip.
    const [searchParams] = useSearchParams();
    const setSearchParam = useSetSearchParam();
    const { input: nameInput, onChange: handleNameChange } = useDebouncedSearchParam('name');
    const namePrefix = searchParams.get('name') ?? '';
    const active = searchParams.get('status') !== 'deleted';
    const includeTest = searchParams.get('test') === '1';
    useEffect(() => {
        if (filter.namePrefix !== namePrefix || filter.active !== active || filter.includeTest !== includeTest) {
            setFilter({ namePrefix, active, includeTest });
        }
    }, [namePrefix, active, includeTest, filter, setFilter]);

    const toggleFavorite = useCallback((e: React.MouseEvent, homeId: string) => {
        e.preventDefault();
        setFavorites((prev) => {
            const next = new Set(prev);
            if (next.has(homeId)) {
                next.delete(homeId);
            } else {
                next.add(homeId);
            }
            saveFavorites(next);
            return next;
        });
    }, []);

    const favoriteIds = useMemo(() => [...favorites].sort((a, b) => a.localeCompare(b)), [favorites]);
    const mainHomes = useMemo(
        () =>
            orderBy(
                homes.filter((home) => !favorites.has(home.id)),
                [(home) => home.name.toLowerCase()],
                ['asc'],
            ),
        [homes, favorites],
    );
    const hasFavorites = favoriteIds.length > 0;

    return (
        <PageShell>
            <PageHeader title="Homes" description="Browse the homes that are currently available in the system.">
                <Input
                    placeholder="Filter by name..."
                    value={nameInput}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-[200px]"
                />
                <Select
                    value={active ? 'active' : 'deleted'}
                    onValueChange={(value) => setSearchParam('status', value === 'active' ? undefined : value)}
                >
                    <SelectTrigger className="w-[140px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="deleted">Deleted</SelectItem>
                    </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={() => setSearchParam('test', includeTest ? undefined : '1')}>
                    {includeTest ? 'Hide test homes' : 'Show test homes'}
                </Button>
            </PageHeader>

            {hasFavorites && (
                <div className="flex flex-col gap-3">
                    <h2 className="flex items-center gap-2 text-lg font-semibold">
                        <Star size={18} className="fill-amber-400 text-amber-400" />
                        Favorites
                    </h2>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {favoriteIds.map((id) => (
                            <FavoriteHomeCard
                                key={id}
                                homeId={id}
                                favorites={favorites}
                                onToggleFavorite={toggleFavorite}
                            />
                        ))}
                    </div>
                </div>
            )}

            {isLoading ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Loading homes...</CardTitle>
                        <CardDescription>Please wait while we fetch the homes.</CardDescription>
                    </CardHeader>
                </Card>
            ) : hasError ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Error loading homes</CardTitle>
                        <CardDescription>Unable to load homes. Please try again later.</CardDescription>
                    </CardHeader>
                </Card>
            ) : mainHomes.length === 0 && !hasFavorites ? (
                <Card>
                    <CardHeader>
                        <CardTitle>{active ? 'No active homes found' : 'No deleted homes found'}</CardTitle>
                        <CardDescription>
                            {active
                                ? 'Once homes are added to the system they will appear in this list.'
                                : 'Deleted homes will appear in this list.'}
                        </CardDescription>
                    </CardHeader>
                </Card>
            ) : mainHomes.length > 0 ? (
                <>
                    {hasFavorites && <h2 className="text-lg font-semibold">All Homes</h2>}
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {mainHomes.map((home) => (
                            <HomeCard key={home.id} home={home} isFavorite={false} onToggleFavorite={toggleFavorite} />
                        ))}
                    </div>
                    {hasMore && (
                        <div className="flex justify-center">
                            <Button variant="outline" onClick={() => loadMore()}>
                                Load More
                            </Button>
                        </div>
                    )}
                </>
            ) : null}
        </PageShell>
    );
};

export default HomeListPage;
